// 确认对话框：替代 window.confirm。用法：const confirm = useConfirm(); if (await confirm({...})) ...
import { createContext, use, useCallback, useRef, useState, type ReactNode } from "react";
import { Button } from "./button";
import { DialogContent, DialogRoot } from "./dialog";

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  /** 需要输入该文本才能确认（用于解散战队等不可恢复操作）。 */
  typeToConfirm?: string;
}

const Ctx = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    setTyped("");
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  const blocked = !!opts?.typeToConfirm && typed !== opts.typeToConfirm;

  return (
    <Ctx value={confirm}>
      {children}
      <DialogRoot open={!!opts} onOpenChange={(o) => !o && close(false)}>
        {opts ? (
          <DialogContent
            title={opts.title}
            maxWidth={480}
            footer={
              <>
                <Button variant="outline" onClick={() => close(false)}>
                  {opts.cancelText ?? "取消"}
                </Button>
                <Button variant={opts.destructive ? "destructive" : "default"} disabled={blocked} onClick={() => close(true)}>
                  {opts.confirmText ?? "确定"}
                </Button>
              </>
            }
          >
            {opts.description ? <div className="body-sm muted">{opts.description}</div> : null}
            {opts.typeToConfirm ? (
              <div className="field">
                <label className="cs-label" htmlFor="confirm-type">
                  输入「{opts.typeToConfirm}」以确认
                </label>
                <input id="confirm-type" className="cs-input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
              </div>
            ) : null}
          </DialogContent>
        ) : null}
      </DialogRoot>
    </Ctx>
  );
}

export function useConfirm() {
  const v = use(Ctx);
  if (!v) throw new Error("useConfirm 必须在 ConfirmProvider 内使用");
  return v;
}
